import html2canvas from 'html2canvas';

/**
 * Service for exporting size charts as images or uploading to Shopify
 */

/**
 * Generate HTML for a size chart based on selected options
 * @param {Object} chart - The size chart data
 * @param {Object} chartStyle - The chart style settings
 * @param {Object} options - Options for which sections to include
 * @returns {string} - HTML string for the chart
 */
export const generateChartHtml = (chart, chartStyle, options) => {
  // Extract values from chart and apply defaults
  const chartData = chart.sizechart?.sizechart || chart.sizechart;
  const chartStyleValue = chartStyle || {};
  const headerTextValue = chart.headerText || '';
  const footerTextValue = chart.footerText || '';
  const infoTextValue = chart.infoText || '';
  const showHeaderImageValue = chart.showHeaderImage || false;
  const headerImageUrlValue = chart.headerImageUrl || '';
  const showFooterImageValue = chart.showFooterImage || false;
  const footerImageUrlValue = chart.footerImageUrl || '';
  const showMeasurementDiagram = chart.showMeasurementDiagram !== false;
  
  
  // Try multiple paths to find garmentType in the data structure
  const garmentType = chart.garmentType || 
                     chartData?.garmentType || 
                     chart.sizechart?.garmentType || 
                     chart.sizechart?.sizechart?.garmentType || 
                     'dress';
  
  const fitType = chart.fitType || 
                 chartData?.fitType || 
                 chart.sizechart?.fitType || 
                 chart.sizechart?.sizechart?.fitType || 
                 'regular';
                 
  const stretchLevel = chart.stretchLevel || 
                      chartData?.stretchLevel || 
                      chart.sizechart?.stretchLevel || 
                      chart.sizechart?.sizechart?.stretchLevel || 
                      'none';
  
  console.log('Export garmentType detection:', {
    'chart.garmentType': chart.garmentType,
    'chartData?.garmentType': chartData?.garmentType,
    'chart.sizechart?.garmentType': chart.sizechart?.garmentType,
    'chart.sizechart?.sizechart?.garmentType': chart.sizechart?.sizechart?.garmentType,
    'final garmentType': garmentType
  });
  
  // Start building the HTML
  let chartHtml = `
    <div style="font-family: ${chartStyleValue.fontFamily || 'sans-serif'}; max-width: 800px; margin: 0 auto; padding: 16px;">
  `;
  
  // Header Text
  if (options.includeHeader && headerTextValue) {
    chartHtml += `
      <div style="margin-bottom: 16px;">
        <h1 style="color: ${chartStyleValue.chartTitleColor || '#212b36'}; font-family: ${chartStyleValue.fontFamily || 'sans-serif'}; margin-top: 0; font-size: 20px; font-weight: 600; line-height: 1.2;">${headerTextValue}</h1>
      </div>
    `;
  }
  
  // Header Image
  if (options.includeHeaderImage && showHeaderImageValue && headerImageUrlValue) {
    chartHtml += `
      <div style="margin-bottom: 16px; text-align: center;">
        <img src="${headerImageUrlValue}" style="max-width: 100%;">
      </div>
    `;
  }
  
  // Fit & Stretch Guide Visualizer
  if (options.includeFitGuideVisualizer) {
    chartHtml += `
      <div style="margin-bottom: 16px; padding: 16px; background-color: ${chartStyleValue.fitGuideBgColor || '#f4f6f8'}; border-radius: 4px;">
        
        <!-- Fit Type Track -->
        <div style="margin-bottom: 16px;">
          <p style="margin-bottom: 8px; color: ${chartStyleValue.fitGuideLabelColor || '#637381'}">Fit Type</p>
          <div style="position: relative; height: 8px; background-color: ${chartStyleValue.fitGuideTrackColor || '#e6e6e6'}; border-radius: 4px; margin-bottom: 24px;">
            <!-- Indicator dot positioned based on fit type -->
            <div style="
              position: absolute;
              ${getFitTypeIndicatorStyle(fitType, chartStyleValue)}
            "></div>
          </div>
          <div style="display: flex; justify-content: space-between;">
            <span style="color: ${chartStyleValue.fitGuideLabelColor || '#637381'}">Slim</span>
            <span style="color: ${chartStyleValue.fitGuideLabelColor || '#637381'}">Regular</span>
            <span style="color: ${chartStyleValue.fitGuideLabelColor || '#637381'}">Loose</span>
            <span style="color: ${chartStyleValue.fitGuideLabelColor || '#637381'}">Oversized</span>
          </div>
        </div>
        
        <!-- Stretch Level Track -->
        <div>
          <p style="margin-bottom: 8px; color: ${chartStyleValue.fitGuideLabelColor || '#637381'}">Stretch Level</p>
          <div style="position: relative; height: 8px; background-color: ${chartStyleValue.fitGuideTrackColor || '#e6e6e6'}; border-radius: 4px; margin-bottom: 24px;">
            <!-- Indicator dot positioned based on stretch level -->
            <div style="
              position: absolute;
              ${getStretchLevelIndicatorStyle(stretchLevel, chartStyleValue)}
            "></div>
          </div>
          <div style="display: flex; justify-content: space-between;">
            <span style="color: ${chartStyleValue.fitGuideLabelColor || '#637381'}">None</span>
            <span style="color: ${chartStyleValue.fitGuideLabelColor || '#637381'}">Slight</span>
            <span style="color: ${chartStyleValue.fitGuideLabelColor || '#637381'}">Moderate</span>
            <span style="color: ${chartStyleValue.fitGuideLabelColor || '#637381'}">Stretchy</span>
            <span style="color: ${chartStyleValue.fitGuideLabelColor || '#637381'}">High</span>
          </div>
        </div>
      </div>
    `;
  }
  
  // Size Chart Table
  if (options.includeSizeTable && chartData?.columns && chartData?.rows) {
    chartHtml += `
      <div style="margin-bottom: 16px; border: 1px solid ${chartStyleValue.borderColor || '#ddd'}; border-radius: 4px; overflow: hidden;">
        <table style="width: 100%; border-collapse: collapse;">
          <thead>
            <tr>
              ${chartData.columns.map(col => `
                <th style="
                  padding: 10px; 
                  text-align: left; 
                  background-color: ${chartStyleValue.headerBgColor || '#f4f6f8'};
                  color: ${chartStyleValue.headerTextColor || '#212b36'};
                  border-bottom: 1px solid ${chartStyleValue.borderColor || '#ddd'};
                ">
                  ${col.name}
                </th>
              `).join('')}
            </tr>
          </thead>
          <tbody>
            ${chartData.rows.map((row, rowIndex) => {
              // Check if we're using the new format - if ANY row has original_size OR country_size, it's new format
              const isNewFormat = row.hasOwnProperty('original_size') || row.hasOwnProperty('country_size') || 
                                chartData.rows.some(r => r.hasOwnProperty('original_size') || r.hasOwnProperty('country_size'));
              
              
              return `
                <tr style="background-color: ${rowIndex % 2 === 1 ? chartStyleValue.rowAlternateColor || '#f9fafb' : 'transparent'};">
                  ${isNewFormat ? 
                    // New format with original_size and country_size
                    (() => {
                      const cells = [];
                      let measurementIndex = 0;
                      
                      chartData.columns.forEach((column, colIndex) => {
                        
                        if (column.key === 'original_size' && row.hasOwnProperty('original_size')) {
                          cells.push(`<td style="padding: 10px; border-bottom: 1px solid ${chartStyleValue.borderColor || '#ddd'}; color: ${rowIndex % 2 === 1 ? chartStyleValue.alternateRowTextColor || '#212b36' : chartStyleValue.cellTextColor || '#212b36'};"><strong>${row.original_size || '-'}</strong></td>`);
                        } else if (column.key === 'country_size' && row.hasOwnProperty('country_size')) {
                          cells.push(`<td style="padding: 10px; border-bottom: 1px solid ${chartStyleValue.borderColor || '#ddd'}; color: ${rowIndex % 2 === 1 ? chartStyleValue.alternateRowTextColor || '#212b36' : chartStyleValue.cellTextColor || '#212b36'};">${row.country_size || '-'}</td>`);
                        } else if (column.key.endsWith('_size')) {
                          // This is a size column (like uk_nz_size, au_size) - get value directly from row property
                          let cellValue = '-';
                          if (row.hasOwnProperty(column.key)) {
                            cellValue = row[column.key];
                          }
                          
                          if (cellValue !== null && cellValue !== undefined && cellValue !== '') {
                            cellValue = cellValue.toString();
                          } else {
                            cellValue = '-';
                          }
                          
                          cells.push(`<td style="padding: 10px; border-bottom: 1px solid ${chartStyleValue.borderColor || '#ddd'}; color: ${rowIndex % 2 === 1 ? chartStyleValue.alternateRowTextColor || '#212b36' : chartStyleValue.cellTextColor || '#212b36'};">${cellValue}</td>`);
                          // Don't increment measurementIndex for size columns
                        } else if (column.key !== 'original_size' && column.key !== 'country_size' && !column.key.endsWith('_size')) {
                          // This is a measurement column - get value directly from row property or from values array
                          let cellValue = '-';
                          
                          // First try to get value directly from row property (for combined charts)
                          if (row.hasOwnProperty(column.key)) {
                            cellValue = row[column.key];
                          } 
                          // Fallback to values array (for legacy format)
                          else if (row.values && Array.isArray(row.values) && measurementIndex >= 0 && measurementIndex < row.values.length) {
                            cellValue = row.values[measurementIndex];
                          }
                          
                          // Ensure cellValue is properly formatted (handle ranges and other values)
                          if (cellValue !== null && cellValue !== undefined && cellValue !== '') {
                            cellValue = cellValue.toString();
                          } else {
                            cellValue = '-';
                          }
                          cells.push(`<td style="padding: 10px; border-bottom: 1px solid ${chartStyleValue.borderColor || '#ddd'}; color: ${rowIndex % 2 === 1 ? chartStyleValue.alternateRowTextColor || '#212b36' : chartStyleValue.cellTextColor || '#212b36'};">${cellValue}</td>`);
                          measurementIndex++;
                        }
                      });
                      
                      return cells.join('');
                    })() :
                    // Old format with just size
                    chartData.columns.map((column, colIndex) => {
                      if (colIndex === 0) {
                        return `<td style="padding: 10px; border-bottom: 1px solid ${chartStyleValue.borderColor || '#ddd'}; color: ${rowIndex % 2 === 1 ? chartStyleValue.alternateRowTextColor || '#212b36' : chartStyleValue.cellTextColor || '#212b36'};"><strong>${row.size || '-'}</strong></td>`;
                      } else {
                        const valueIndex = colIndex - 1;
                        return `<td style="padding: 10px; border-bottom: 1px solid ${chartStyleValue.borderColor || '#ddd'}; color: ${rowIndex % 2 === 1 ? chartStyleValue.alternateRowTextColor || '#212b36' : chartStyleValue.cellTextColor || '#212b36'};">${valueIndex >= 0 && row.values && valueIndex < row.values.length ? row.values[valueIndex] : '-'}</td>`;
                      }
                    }).join('')
                  }
                </tr>
              `;
            }).join('')}
          </tbody>
        </table>
      </div>
    `;
  }
  
  // How to Measure Section
  if (options.includeHowToMeasure && showMeasurementDiagram) {
    chartHtml += `
      <div style="margin-bottom: 16px; padding: 16px; background-color: ${chartStyleValue.measurementBgColor || '#f4f6f8'}; border-radius: 4px;">
        <h3 style="margin-top: 0; margin-bottom: 12px; color: ${chartStyleValue.measurementHeadingColor || '#212b36'}; font-family: ${chartStyleValue.fontFamily || 'sans-serif'};">How to Measure</h3>
        
        <div style="display: flex; flex-wrap: wrap;">
          <!-- Measurement Diagram -->
          <div style="flex: 1; min-width: 250px; text-align: center; margin-bottom: 16px;">
            <img src="/images/sizecharts/${garmentType}.png" style="max-width: 100%; height: auto;" alt="Measurement diagram" />
          </div>
          
          <!-- Measurement Points -->
          <div style="flex: 2; min-width: 250px;">
            <div style="display: flex; flex-wrap: wrap;">
              ${getMeasurementPointsHtml(garmentType, chartStyleValue)}
            </div>
          </div>
        </div>
      </div>
    `;
  }
  
  // Fit Guide Information
  if (options.includeFitGuide && chartData?.fit_info && chartData.fit_info.length > 0) {
    chartHtml += `
      <div style="margin-bottom: 16px; padding: 16px; background-color: ${chartStyleValue.fitGuideBgColor || '#f4f6f8'}; border-radius: 4px;">
        <h3 style="margin-top: 0; margin-bottom: 12px; font-family: ${chartStyleValue.fontFamily || 'sans-serif'};">Fit Guide:</h3>
        ${chartData.fit_info.map(fitItem => {
          // Determine which size to display
          const sizeDisplay = fitItem.original_size || fitItem.country_size || fitItem.size || '';
          
          return `
            <div style="margin-bottom: 12px; padding: 12px; background-color: ${chartStyleValue.fitGuideCardBgColor || '#ffffff'}; border-radius: 4px; border-left: 4px solid ${chartStyleValue.fitGuideAccentColor || '#0068d9'};">
              <div style="display: flex; justify-content: space-between; align-items: center;">
                <strong>${sizeDisplay}</strong>
                ${fitItem.fit_tag ? `<span style="background-color: ${chartStyleValue.fitGuideTagBgColor || '#f2f7fe'}; color: ${chartStyleValue.fitGuideTagTextColor || '#0068d9'}; padding: 2px 8px; border-radius: 12px; font-size: 13px;">${fitItem.fit_tag}</span>` : ''}
              </div>
              ${(fitItem.description || fitItem.fit_description) ? `<p style="margin-top: 8px; margin-bottom: 4px;">${fitItem.description || fitItem.fit_description}</p>` : ''}
              ${fitItem.buyer_hint ? `<p style="margin-top: 4px; margin-bottom: 4px; font-style: italic; color: #637381;">${fitItem.buyer_hint}</p>` : ''}
            </div>
          `;
        }).join('')}
      </div>
    `;
  }
  
  // Notes Section
  if (options.includeNotes && chartData?.notes && chartData.notes.length > 0) {
    chartHtml += `
      <div style="margin-bottom: 16px; background-color: #f9fafb; padding: 12px; border-radius: 4px;">
        <h3 style="margin-top: 0; margin-bottom: 8px; font-family: ${chartStyleValue.fontFamily || 'sans-serif'};">${chartData.notesTitle || 'Notes:'}</h3>
        <ul style="margin-top: 8px; padding-left: 20px;">
          ${chartData.notes.map(note => `<li style="margin-bottom: 4px;">${note}</li>`).join('')}
        </ul>
      </div>
    `;
  }
  
  // Footer Text
  if (options.includeFooter && footerTextValue) {
    chartHtml += `<div style="margin-bottom: 16px; margin-left: 16px; font-family: ${chartStyleValue.fontFamily || 'sans-serif'}; color: #212b36;">${footerTextValue}</div>`;
  }
  
  // Footer Image
  if (options.includeFooterImage && showFooterImageValue && footerImageUrlValue) {
    chartHtml += `<div style="text-align: center; margin-bottom: 16px;"><img src="${footerImageUrlValue}" style="max-width: 100%;"></div>`;
  }
  
  // Info Text
  if (options.includeInfoText && infoTextValue) {
    chartHtml += `<div style="padding: 12px; background-color: #f9fafb; border-radius: 4px; font-family: ${chartStyleValue.fontFamily || 'sans-serif'};"><p style="margin: 0;">${infoTextValue}</p></div>`;
  }
  
  // Close the container div
  chartHtml += `</div>`;
  
  return chartHtml;
};

/**
 * Export a size chart as an image
 * @param {Object} chart - The size chart data
 * @param {Object} chartStyle - The chart style settings
 * @param {Object} options - Options for which sections to include
 * @param {string} [customFilename] - Optional custom filename provided by the user
 * @returns {Promise} - Resolves with true if successful
 */
export const exportChartAsImage = async (chart, chartStyle, options, customFilename) => {
  try {
    console.log('Exporting chart with data:', {
      headerText: chart.headerText,
      footerText: chart.footerText,
      infoText: chart.infoText,
      showHeaderImage: chart.showHeaderImage,
      headerImageUrl: chart.headerImageUrl,
      showFooterImage: chart.showFooterImage,
      footerImageUrl: chart.footerImageUrl
    });
    console.log('Export options:', options);
    
    // Create a temporary div to render the chart
    const tempDiv = document.createElement('div');
    tempDiv.style.position = 'absolute';
    tempDiv.style.left = '-9999px';
    tempDiv.style.width = '800px'; // Fixed width for consistent output
    document.body.appendChild(tempDiv);
    
    // Generate the HTML for the chart
    const chartHtml = generateChartHtml(chart, chartStyle, options);
    tempDiv.innerHTML = chartHtml;
    
    // Function to preload all images in the container
    const preloadImages = async (container) => {
      const imgElements = container.querySelectorAll('img');
      console.log(`Found ${imgElements.length} images to preload`);
      
      if (imgElements.length === 0) {
        return Promise.resolve();
      }
      
      const imagePromises = Array.from(imgElements).map(img => {
        console.log('Preloading image:', img.src);
        return new Promise((resolve) => {
          if (img.complete) {
            console.log('Image already loaded:', img.src);
            resolve();
          } else {
            img.onload = () => {
              console.log('Image loaded successfully:', img.src);
              resolve();
            };
            img.onerror = () => {
              console.error('Failed to load image:', img.src);
              // Continue without the image
              resolve();
            };
          }
        });
      });
      
      return Promise.all(imagePromises);
    };
    
    // Wait for all images to load before generating the canvas
    await preloadImages(tempDiv);
    
    // Use html2canvas to convert to image
    const canvas = await html2canvas(tempDiv, {
      allowTaint: true,
      useCORS: true,
      scale: 2, // Higher scale for better quality
      backgroundColor: '#ffffff',
      logging: true, // Enable logging for debugging
      imageTimeout: 15000 // Increase timeout for image loading
    });
    
    // Convert canvas to image and trigger download
    const image = canvas.toDataURL('image/png');
    const link = document.createElement('a');
    link.href = image;
    
    // Use custom filename if provided, otherwise generate a meaningful name
    let chartName = customFilename || 'sizechart';
    
    // If no custom filename is provided, try to generate a meaningful name
    if (!customFilename) {
      if (chart.name) {
        chartName = chart.name;
      } else if (chart.sizechart?.name) {
        chartName = chart.sizechart.name;
      } else if (chart.sizechart?.sizechart?.name) {
        chartName = chart.sizechart.sizechart.name;
      } else if (chart.productCategory) {
        // Use product category if no name is available
        chartName = `sizechart_${chart.productCategory.replace(/\s+/g, '_').toLowerCase()}`;
      }
    }
    
    // Sanitize the filename to remove any invalid characters
    chartName = chartName.replace(/[\\/:\*\?\"<>\|]/g, '_');
    
    link.download = `${chartName}.png`;
    link.click();
    
    // Clean up
    document.body.removeChild(tempDiv);
    
    return true;
  } catch (error) {
    console.error('Error exporting chart as image:', error);
    throw error;
  }
};

/**
 * Upload a size chart to Shopify
 * @param {Object} chart - The size chart data
 * @param {Object} chartStyle - The chart style settings
 * @param {Object} options - Options for which sections to include
 * @param {string} shopId - The shop ID
 * @param {string} accessToken - The shop access token
 * @returns {Promise} - Resolves with the upload result
 */
export const uploadChartToShopify = async (chart, chartStyle, options, shopId, accessToken) => {
  try {
    // Import the upload service dynamically to avoid circular dependencies
    const { uploadSizeChartToShopify } = await import('./sizeChartUploadService');
    
    console.log('Uploading chart to Shopify with data:', {
      headerText: chart.headerText,
      footerText: chart.footerText,
      infoText: chart.infoText,
      showHeaderImage: chart.showHeaderImage,
      headerImageUrl: chart.headerImageUrl,
      showFooterImage: chart.showFooterImage,
      footerImageUrl: chart.footerImageUrl
    });
    console.log('Upload options:', options);
    
    // Create a temporary div to render the chart
    const tempDiv = document.createElement('div');
    tempDiv.style.position = 'absolute';
    tempDiv.style.left = '-9999px';
    tempDiv.style.width = '800px'; // Fixed width for consistent output
    document.body.appendChild(tempDiv);
    
    // Generate the HTML for the chart
    const chartHtml = generateChartHtml(chart, chartStyle, options);
    tempDiv.innerHTML = chartHtml;
    
    // Function to preload all images in the container
    const preloadImages = async (container) => {
      const imgElements = container.querySelectorAll('img');
      console.log(`Found ${imgElements.length} images to preload for Shopify upload`);
      
      if (imgElements.length === 0) {
        return Promise.resolve();
      }
      
      const imagePromises = Array.from(imgElements).map(img => {
        console.log('Preloading image for Shopify upload:', img.src);
        return new Promise((resolve) => {
          if (img.complete) {
            console.log('Image already loaded for Shopify upload:', img.src);
            resolve();
          } else {
            img.onload = () => {
              console.log('Image loaded successfully for Shopify upload:', img.src);
              resolve();
            };
            img.onerror = () => {
              console.error('Failed to load image for Shopify upload:', img.src);
              // Continue without the image
              resolve();
            };
          }
        });
      });
      
      return Promise.all(imagePromises);
    };
    
    // Wait for all images to load before generating the canvas
    await preloadImages(tempDiv);
    
    // Use html2canvas to convert to image
    const canvas = await html2canvas(tempDiv, {
      allowTaint: true,
      useCORS: true,
      scale: 2, // Higher scale for better quality
      backgroundColor: '#ffffff',
      logging: true, // Enable logging for debugging
      imageTimeout: 15000 // Increase timeout for image loading
    });
    
    // Convert canvas to image
    const image = canvas.toDataURL('image/png');
    
    // Generate a meaningful filename
    let chartName = 'sizechart';
    if (chart.name) {
      chartName = chart.name;
    } else if (chart.sizechart?.name) {
      chartName = chart.sizechart.name;
    } else if (chart.sizechart?.sizechart?.name) {
      chartName = chart.sizechart.sizechart.name;
    } else if (chart.productCategory) {
      chartName = `sizechart_${chart.productCategory.replace(/\s+/g, '_').toLowerCase()}`;
    }
    
    // Sanitize the filename
    chartName = chartName.replace(/[\\/:\*\?\"<>\|]/g, '_');
    
    // Validate the access token
    if (!accessToken) {
      throw new Error('Shop access token is required for uploading to Shopify.');
    }
    
    // Upload the image to Shopify using the GraphQL API
    console.log('Uploading size chart to Shopify with name:', chartName);
    const uploadResult = await uploadSizeChartToShopify(image, shopId, accessToken, chartName);
    
    // Log the upload result
    console.log('Size chart upload result:', {
      success: uploadResult.success,
      imageUrl: uploadResult.imageUrl || uploadResult.url,
      fileId: uploadResult.fileId
    });
    
    if (!uploadResult.success) {
      console.error('Failed to upload chart to Shopify:', uploadResult.error);
      throw new Error(uploadResult.error || 'Failed to upload chart to Shopify');
    }
    
    // Clean up
    document.body.removeChild(tempDiv);
    
    return {
      success: true,
      message: 'Chart uploaded to Shopify successfully!',
      imageUrl: uploadResult.url, // The URL property from the upload service
      fileId: uploadResult.fileId
    };
  } catch (error) {
    console.error('Error uploading chart to Shopify:', error);
    throw error;
  }
};

// Helper functions

/**
 * Get the CSS style for the fit type indicator
 * @param {string} fitType - The fit type (slim, regular, loose, oversized)
 * @param {Object} chartStyle - The chart style settings
 * @returns {string} - CSS style string
 */
function getFitTypeIndicatorStyle(fitType, chartStyle) {
  const indicatorShape = chartStyle.fitGuideIndicatorShape || 'dot';
  const indicatorSize = parseInt(chartStyle.fitGuideIndicatorSize || 16);
  const indicatorOffset = parseInt(chartStyle.fitGuideIndicatorOffset || 0);
  const dotColor = chartStyle.fitGuideDotColor || '#000000';
  
  // Map fit type to position percentage
  let position;
  switch (fitType) {
    case 'slim': position = '0%'; break;
    case 'regular': position = '33%'; break;
    case 'loose': position = '67%'; break;
    case 'oversized': position = '100%'; break;
    default: position = '33%'; // Default to regular
  }
  
  // Base style for all shapes - properly handle vertical offset
  let style = `
    left: ${position};
    top: calc(50% + ${indicatorOffset}px);
    transform: translate(-50%, -50%);
    background-color: ${dotColor};
    z-index: 5;
  `;
  
  // Add shape-specific styles
  switch (indicatorShape) {
    case 'rectangle':
      style += `
        width: ${indicatorSize}px;
        height: ${indicatorSize / 2}px;
        margin-top: ${indicatorOffset}px;
      `;
      break;
    case 'triangle':
      style += `
        width: 0;
        height: 0;
        border-left: ${indicatorSize / 2}px solid transparent;
        border-right: ${indicatorSize / 2}px solid transparent;
        border-top: ${indicatorSize}px solid ${dotColor};
        background-color: transparent;
        margin-top: ${indicatorOffset}px;
      `;
      break;
    case 'diamond':
      style += `
        width: ${indicatorSize}px;
        height: ${indicatorSize}px;
        transform: translate(-50%, -50%) rotate(45deg);
        margin-top: ${indicatorOffset}px;
      `;
      break;
    case 'dot':
    default:
      style += `
        width: ${indicatorSize}px;
        height: ${indicatorSize}px;
        border-radius: 50%;
        margin-top: ${indicatorOffset}px;
      `;
      break;
  }
  
  return style;
}

/**
 * Get the CSS style for the stretch level indicator
 * @param {string} stretchLevel - The stretch level (none, slight, moderate, stretchy, high)
 * @param {Object} chartStyle - The chart style settings
 * @returns {string} - CSS style string
 */
function getStretchLevelIndicatorStyle(stretchLevel, chartStyle) {
  const indicatorShape = chartStyle.fitGuideIndicatorShape || 'dot';
  const indicatorSize = parseInt(chartStyle.fitGuideIndicatorSize || 16);
  const indicatorOffset = parseInt(chartStyle.fitGuideIndicatorOffset || 0);
  const dotColor = chartStyle.fitGuideDotColor || '#000000';
  
  // Map stretch level to position percentage
  let position;
  switch (stretchLevel) {
    case 'none': position = '0%'; break;
    case 'slight': position = '25%'; break;
    case 'moderate': position = '50%'; break;
    case 'stretchy': position = '75%'; break;
    case 'high': position = '100%'; break;
    default: position = '50%'; // Default to moderate
  }
  
  // Base style for all shapes - properly handle vertical offset
  let style = `
    left: ${position};
    top: calc(50% + ${indicatorOffset}px);
    transform: translate(-50%, -50%);
    background-color: ${dotColor};
    z-index: 5;
  `;
  
  // Add shape-specific styles
  switch (indicatorShape) {
    case 'rectangle':
      style += `
        width: ${indicatorSize}px;
        height: ${indicatorSize / 2}px;
        margin-top: ${indicatorOffset}px;
      `;
      break;
    case 'triangle':
      style += `
        width: 0;
        height: 0;
        border-left: ${indicatorSize / 2}px solid transparent;
        border-right: ${indicatorSize / 2}px solid transparent;
        border-top: ${indicatorSize}px solid ${dotColor};
        background-color: transparent;
        margin-top: ${indicatorOffset}px;
      `;
      break;
    case 'diamond':
      style += `
        width: ${indicatorSize}px;
        height: ${indicatorSize}px;
        transform: translate(-50%, -50%) rotate(45deg);
        margin-top: ${indicatorOffset}px;
      `;
      break;
    case 'dot':
    default:
      style += `
        width: ${indicatorSize}px;
        height: ${indicatorSize}px;
        border-radius: 50%;
        margin-top: ${indicatorOffset}px;
      `;
      break;
  }
  
  return style;
}

/**
 * Get HTML for measurement points based on garment type
 * @param {string} garmentType - The garment type
 * @param {Object} chartStyle - The chart style settings
 * @returns {string} - HTML string for measurement points
 */
function getMeasurementPointsHtml(garmentType, chartStyle) {
  // Import measurement points from garmentTemplateService
  const { measurementPoints } = require('./garmentTemplateService');
  
  // Get the measurement points for the specific garment type
  const points = measurementPoints[garmentType] || measurementPoints['dress']; // Fallback to dress if type not found
  
  // Generate HTML for each point
  return points.map((point, index) => `
    <div style="display: flex; align-items: flex-start; margin-bottom: 12px; width: 100%;">
      <div style="
        width: 24px;
        height: 24px;
        border-radius: 50%;
        background-color: ${chartStyle.measurementNumberBgColor || '#0068d9'};
        color: ${chartStyle.measurementNumberColor || '#ffffff'};
        display: flex;
        align-items: center;
        justify-content: center;
        margin-right: 12px;
        flex-shrink: 0;
      ">${index + 1}</div>
      <div>
        <strong style="display: block; color: ${chartStyle.measurementHeadingColor || '#212b36'};">${point.label}</strong>
        <span style="color: ${chartStyle.measurementTextColor || '#637381'};">${point.description}</span>
      </div>
    </div>
  `).join('');
}
